import * as Popover from "@radix-ui/react-popover";
import { COMMENT_TYPE } from "store/apis/notes";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { OptionDropdown } from "components/icons/optionDropdown";
import { Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  FinancialClosingDetail,
  useDeleteAllFinancialClosingVersionsMutation,
  useDeleteFinancialClosingMutation,
  useUpdateFinancialClosingStatusMutation,
} from "store/apis/financialClosing";
import { FINANCIAL_CLOSING_CARD_TYPE } from "constants/chatType";
import { CLOSING_STATUS } from "constants/financialClosing";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useAppDispatch } from "hooks/useAppDispatch";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { setChatCardAttachment } from "store/slices/financialClosing";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { FinalizeFinancialConfirmationModal } from "pages/Books/FinancialClosing/FinalizeFinancialConfirmationModal";
import { AmendClosingModal } from "pages/Books/FinancialClosing/AmendClosingModal";
import { DeleteAllVersionsModal } from "pages/Books/FinancialClosing/DeleteAllVersionsModal";
import { RequestChangesConfirmationModal } from "pages/Books/FinancialClosing/RequestChangesConfirmationModal";

export const FinancialClosingListAction = ({
  financialClosingItem,
}: {
  financialClosingItem: FinancialClosingDetail;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const [deleteClosing] = useDeleteFinancialClosingMutation();
  const dispatch = useAppDispatch();
  const { entities } = useCurrentGroupContext();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const channelId = entities.find(
    ({ uuid }) => uuid === entityId
  )?.bookkeeping_channel;

  const deleteModal = useModal();
  const reviewModal = useModal();
  const amendModal = useModal();
  const requestChangesModal = useModal();
  const deleteAllModal = useModal();

  const [updateStatus, { isLoading: isReviewing }] =
    useUpdateFinancialClosingStatusMutation();
  const [deleteAllVersions, { isLoading: isDeletingAll }] =
    useDeleteAllFinancialClosingVersionsMutation();

  const { status, version } = financialClosingItem;
  const isReviewPending = status === CLOSING_STATUS.REVIEW_PENDING;
  const isReadOnlyRow = status === CLOSING_STATUS.AMENDED;

  const showMarkAsReviewed = !isReadOnlyRow && isReviewPending && isAdmin;
  const showRaiseClarification = !isReadOnlyRow && isReviewPending;
  const showRequestChanges = !isReadOnlyRow && isReviewPending && isCustomer;
  const showAmend =
    !isReadOnlyRow &&
    !isCustomer &&
    (isReviewPending || status === CLOSING_STATUS.COMPLETED);
  const showDelete = !isReadOnlyRow && version === 1;
  const showDeleteAll = !isReadOnlyRow && !isCustomer && version > 1;

  const onMarkAsReviewed = async (sendEmail: boolean) => {
    try {
      await updateStatus({
        entityId,
        financialClosingId: financialClosingItem.uuid,
        payload: {
          status: CLOSING_STATUS.COMPLETED,
          send_completed_financials_email: sendEmail,
        },
      }).unwrap();
      successToast({ message: "Financials marked as reviewed" });
      reviewModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onRequestChanges = async (reason: string) => {
    try {
      await updateStatus({
        entityId,
        financialClosingId: financialClosingItem.uuid,
        payload: { status: CLOSING_STATUS.REJECTED, rejection_reason: reason },
      }).unwrap();
      successToast({ message: "Your request for changes has been sent" });
      requestChangesModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const onDeleteAllVersions = async () => {
    try {
      await deleteAllVersions({
        entityId,
        financialClosingId: financialClosingItem.uuid,
      }).unwrap();
      successToast({ message: "All versions deleted" });
      deleteAllModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const onDelete = async () => {
    try {
      await deleteClosing({
        entityId,
        financialClosingId: financialClosingItem.uuid,
      }).unwrap();
      successToast({ message: "Financial closing deleted" });
      deleteModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onRaiseClarification = async () => {
    try {
      if (!channelId) {
        return alertToast({
          message: "No chat channel found for this entity",
        });
      }

      dispatch(
        setChatCardAttachment({
          entityId: entityId,
          financialClosingId: financialClosingItem.uuid,
          card_type: FINANCIAL_CLOSING_CARD_TYPE.CLARIFICATION_RAISED,
          start_date: financialClosingItem.start_date,
          end_date: financialClosingItem.end_date,
        })
      );
      dispatch(openFloatingChat());
      dispatch(setToOpenChatId(channelId));
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (!hasWritePermission) {
    return null;
  }

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="all:unset"
      >
        <AddCommentPopoverRoot contentId={financialClosingItem.uuid}>
          <Dropdown.Root>
            <Modal.Root
              open={deleteModal.isOpen}
              onOpenChange={deleteModal.close}
            >
              <Dropdown.Trigger asChild>
                <Popover.Anchor asChild>
                  <div>
                    <Button customType="ghost_icon" size="xsmall">
                      <span className="t-text-text-30">
                        <OptionDropdown color="currentColor" />
                      </span>
                    </Button>
                  </div>
                </Popover.Anchor>
              </Dropdown.Trigger>
              <Popover.Portal>
                <AddCommentPop
                  commentType={COMMENT_TYPE.FINANCIAL_CLOSING_COMMENT}
                  contentId={financialClosingItem.uuid}
                />
              </Popover.Portal>
              <Dropdown.Content align="end" className="t-w-40">
                {showMarkAsReviewed && (
                  <Dropdown.Item onSelect={reviewModal.open}>
                    Mark as reviewed
                  </Dropdown.Item>
                )}

                {showAmend && (
                  <Dropdown.Item onSelect={amendModal.open}>
                    Amend
                  </Dropdown.Item>
                )}

                {showRequestChanges && (
                  <Dropdown.Item onSelect={requestChangesModal.open}>
                    Request changes
                  </Dropdown.Item>
                )}

                <Popover.Trigger asChild>
                  <Dropdown.Item>Add comment</Dropdown.Item>
                </Popover.Trigger>

                {showRaiseClarification && (
                  <Dropdown.Item onSelect={onRaiseClarification}>
                    Raise clarification
                  </Dropdown.Item>
                )}

                {showDelete && (
                  <Modal.Trigger asChild>
                    <Dropdown.Item type="danger" onSelect={deleteModal.open}>
                      Delete
                    </Dropdown.Item>
                  </Modal.Trigger>
                )}

                {showDeleteAll && (
                  <Dropdown.Item type="danger" onSelect={deleteAllModal.open}>
                    Delete all versions
                  </Dropdown.Item>
                )}
              </Dropdown.Content>
              <Formik initialValues={{}} onSubmit={onDelete}>
                {({ isSubmitting, submitForm }) => (
                  <Modal.Content>
                    <Modal.Header>
                      <Modal.Title>Delete financial closing</Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-text-body-sm  t-text-text-100">
                      Are you sure you want to delete this financial closing?
                      <br />
                      This cannot be undone.
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button disabled={isSubmitting}>Cancel</Button>
                      </Modal.RawClose>
                      <Button
                        customType="danger"
                        onClick={submitForm}
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                      >
                        Delete
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Modal.Content>
                )}
              </Formik>
            </Modal.Root>
          </Dropdown.Root>
        </AddCommentPopoverRoot>

        <AmendClosingModal
          open={amendModal.isOpen}
          onClose={amendModal.close}
          entityId={entityId}
          financialClosingId={financialClosingItem.uuid}
        />
        <DeleteAllVersionsModal
          open={deleteAllModal.isOpen}
          isLoading={isDeletingAll}
          onCancel={deleteAllModal.close}
          onConfirm={onDeleteAllVersions}
        />
      </button>

      <RequestChangesConfirmationModal
        show={requestChangesModal.isOpen}
        onClose={requestChangesModal.close}
        onConfirm={onRequestChanges}
        period={financialClosingItem.period}
        isLoading={isReviewing}
      />

      <FinalizeFinancialConfirmationModal
        show={reviewModal.isOpen}
        onClose={reviewModal.close}
        period={financialClosingItem.period}
        onConfirm={onMarkAsReviewed}
        isLoading={isReviewing}
        showEmailCheckbox
      />
    </>
  );
};
